// eslint-disable-next-line no-restricted-imports
import { useEffect, useState } from 'react';
import { Box, Card, Flex, GridAlpha, Select, useToasts } from '@applyboard/crystal-ui';
import { useCurrentUser, useEventTracker } from '@applyboard/ui-utils';
import { useGetTestVouchers } from '../../data/testVouchers';
import { PurchaseTestVouchersModal } from './PurchaseTestVouchersModal';
import VoucherStatistics from './VoucherStatistics';
import VouncherTable, { VoucherTableEventsType } from './VoucherTable';
import {
  STATUS_LABEL_MAP,
  TestVoucherStatuses,
  TestVoucherTypes,
  VOUCHER_TYPE_LABEL_MAP,
} from './types';

export enum VoucherActionType {
  PURCHASE,
  TYPE,
  STATUS,
}

export type VoucherActionEvent = {
  eventType: VoucherActionType;
  value: string | null;
};

export const DEFAULT_PAGE_SIZE = 10;

function VoucherDisplay() {
  const [currentUser] = useCurrentUser();
  const toast = useToasts();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [voucherType, setVoucherType] = useState('All Types');
  const [voucherStatus, setVoucherStatus] = useState('All Status');
  const voucherStatusFilter = voucherStatus === 'All Status' ? undefined : voucherStatus;
  const voucherTypeFilter = voucherType === 'All Types' ? undefined : voucherType;
  const {
    testVouchers,
    statistics,
    testVouchersError,
    totalCount,
    isLoadingTestVouchers,
    refetch,
  } = useGetTestVouchers({
    pagination: {
      pageNum: currentPage,
      size: pageSize,
    },
    statusFilter: voucherStatusFilter,
    typeFilter: voucherTypeFilter,
  });
  const { trackEvent } = useEventTracker();

  useEffect(() => {
    if (testVouchersError) {
      console.log('Unable to get the test vouchers', testVouchersError);
      toast.negative(new Error('Unable to get the test vouchers, please try again later'));
    }
  }, [toast, testVouchersError]);

  return (
    <Card>
      <Box p={4}>
        <Flex direction="column" gap={4}>
          {/* Action Bar Controls */}
          <GridAlpha columns="repeat(10, 1fr)" rowGap={8} columnGap={8}>
            <GridAlpha.Item columnStart={{ xs: 1, md: 1 }} columnEnd={{ xs: 11, md: 4 }}>
              <PurchaseTestVouchersModal />
            </GridAlpha.Item>
            <GridAlpha.Item columnStart={{ xs: 1, md: 7 }} columnEnd={11}>
              <Flex direction="row" gap={{ xs: 2, md: 4 }} justify={{ xs: 'start', md: 'end' }}>
                <Select
                  appearance="native"
                  aria-label="select-voucher-type"
                  intent="secondary"
                  width="hug"
                  onChange={(value: string) => {
                    setVoucherType(value);
                    trackEvent({
                      event: 'VAS - Vas Hub Page - Voucher Management - Filter by Voucher type',
                      data: { rp_id: currentUser?.id, voucher_type: value },
                    });
                  }}
                  value={voucherType}
                >
                  <Select.Option key="all-types" label="All Types" value="All Types" />
                  {TestVoucherTypes.map((type: string, index: number) => (
                    <Select.Option
                      key={`type-${index}`}
                      label={VOUCHER_TYPE_LABEL_MAP[type]}
                      value={type}
                    />
                  ))}
                </Select>
                <Select
                  appearance="native"
                  aria-label="select-voucher-status"
                  intent="secondary"
                  width="hug"
                  onChange={(value: string) => {
                    setVoucherStatus(value);
                    trackEvent({
                      event: 'VAS - Vas Hub Page - Voucher Management - Filter by Status',
                      data: { rp_id: currentUser?.id, status: value },
                    });
                  }}
                  value={voucherStatus}
                >
                  <Select.Option key="all-status" label="All Status" value="All Status" />
                  {TestVoucherStatuses.map((status: string, index: number) => (
                    <Select.Option
                      key={`status-${index}`}
                      label={STATUS_LABEL_MAP[status]}
                      value={status}
                    />
                  ))}
                </Select>
              </Flex>
            </GridAlpha.Item>
          </GridAlpha>

          <VoucherStatistics statistics={statistics} loading={isLoadingTestVouchers} />
          <VouncherTable
            testVouchers={testVouchers}
            totalTestVoucher={totalCount}
            loading={isLoadingTestVouchers}
            onChange={async (event: VoucherTableEventsType, value: unknown) => {
              if (['assigned', 'unassigned'].includes(event)) {
                await refetch();
              }

              if (event === 'currentPage') {
                setCurrentPage(value as number);
              }

              if (event === 'pageSize') {
                setPageSize(value as number);
              }
            }}
          />
        </Flex>
      </Box>
    </Card>
  );
}

export default VoucherDisplay;
